import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useContext, useEffect, useRef, useState } from "react";
import { LuClock } from "react-icons/lu";
import { MdOutlineSendToMobile } from "react-icons/md";
import { useIntl } from "react-intl";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import PropagateLoader from "react-spinners/PropagateLoader";
import { ToastContainer, toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../config/redux";
import { AuthContext } from "../../../routes/AuthProvider";
import { isNonEmptyString } from "../../../utils/ValidationHelp";
import { InputField } from "../../common/component/InputField";
import { getFormThunk } from "../../quote/redux/quoteThunks";
import { sendOTPThunk, verifyOTPThunk } from "../redux/phoneThunks";
import OTPInput from "./OTPInput";

export const Phone = () => {
  const intl = useIntl();
  const [value, setValue] = useState("");
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    billingTo: "",
    billingAddress: "",
    siteAddress: "",
  });
  const [phoneError, setPhoneError] = useState("");
  const [triggerChecking, setTriggerChecking] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const captchaRef = useRef<HCaptcha>(null);
  const dispatch = useDispatch<AppDispatch>();
  const state = useContext(AuthContext);

  const { loading } = useSelector((state: RootState) => state.phone);

  const handleVerificationSuccess = (token: string, ekey: string) => {
    dispatch(sendOTPThunk({ phoneNumber: value })).then((res) => {
      if (res.payload.code === 200) {
        setIsPhoneVerified(true);
        setTimer(60);
        setCanResend(false);
      } else {
        toast.error(intl.formatMessage({ id: "failToSendOTP" }));
      }
    });
  };

  useEffect(() => {
    setPhoneError((prev) =>
      isNonEmptyString(prev)
        ? intl.formatMessage({ id: "invalidPhoneNumber" })
        : prev
    );
  }, [intl.locale]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTriggerChecking(true);
    const isFieldFilled = Object.values(userDetails).every((value) =>
      isNonEmptyString(value)
    );
    if (value.length < 9)
      setPhoneError(intl.formatMessage({ id: "invalidPhoneNumber" }));

    if (captchaRef.current && value.length >= 9 && isFieldFilled) {
      captchaRef.current.execute();
    }
  };

  const handleComplete = (otp: string) => {
    dispatch(verifyOTPThunk({ phoneNumber: value, otp: parseInt(otp) })).then(
      (res) => {
        if (res.payload.code === 200) {
          state?.login({ phoneNumber: value, ...userDetails });
        } else {
          toast.error(
            intl.formatMessage({
              id: res.payload.code === 11005 ? "otpExpired" : "invalidOTP",
            })
          );
        }
      }
    );
  };

  useEffect(() => {
    dispatch(getFormThunk());
  }, []);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-grow">
      <ToastContainer
        position="top-center"
        hideProgressBar={true}
        newestOnTop={true}
        autoClose={5000}
      />
      <HCaptcha
        ref={captchaRef}
        sitekey="fb680aec-c800-42c0-9134-a6c8066a63ca"
        size="invisible"
        onVerify={handleVerificationSuccess}
      />
      {isPhoneVerified ? (
        <div className="bg-[#c2b39d] flex items-stretch justify-center flex-col p-4 flex-grow text-[#6e5f49] text-lg">
          <h1 className="mb-6 text-3xl font-bold text-center text-white">
            {intl.formatMessage({ id: "enterYourOTP" })}
          </h1>
          <div className="mb-6 text-center">
            {intl.formatMessage({ id: "pleaseEnterOTP" }, { X: value })}
          </div>
          <OTPInput length={5} onComplete={handleComplete} disabled={loading} />

          <div className="flex items-center justify-center mt-6">
            {loading ? (
              <div className="py-3 text-center">
                <PropagateLoader color="#e2edf1" />
              </div>
            ) : canResend ? (
              <p
                className="flex items-center justify-center text-blue-700 transition duration-300 ease-in-out transform cursor-pointer hover:text-blue-950 hover:scale-110"
                onClick={() => {
                  captchaRef.current?.execute();
                }}
              >
                <MdOutlineSendToMobile className="me-2" size={25} />
                {intl.formatMessage({ id: "resendOTP" })}
              </p>
            ) : (
              <p className="flex items-center justify-center">
                <LuClock className="me-2" />
                {intl.formatMessage({ id: "resendAvailable" }, { X: timer })}
              </p>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="bg-[#c2b39d] flex items-stretch justify-center flex-col p-4 flex-grow">
            <div className="w-full mx-auto max-w-96">
              <div className="mb-6 text-2xl font-bold text-center text-white">
                {intl.formatMessage({ id: "requestQuotation" })}
              </div>
              <InputField
                onChange={(value) => {
                  setUserDetails((prev) => ({
                    ...prev,
                    name: (value ?? "").toString(),
                  }));
                }}
                value={userDetails.name}
                placeholder={intl.formatMessage({ id: "name" })}
                triggerChecking={triggerChecking}
              />
              <div className="mb-4">
                <label className="block text-[#6e5f49]">
                  {intl.formatMessage({ id: "contactNo" })}
                </label>
                <div
                  className={`border-2 ${
                    isNonEmptyString(phoneError)
                      ? "border-red-900"
                      : "border-[#5f523d]"
                  }  rounded`}
                >
                  <PhoneInput
                    country={"my"}
                    value={value}
                    onChange={(phone) => {
                      setValue(phone);
                      setPhoneError("");
                    }}
                  />
                </div>
                {isNonEmptyString(phoneError) && (
                  <div className="text-red-900">{phoneError}</div>
                )}
              </div>
              <InputField
                onChange={(value) => {
                  setUserDetails((prev) => ({
                    ...prev,
                    email: (value ?? "").toString(),
                  }));
                }}
                value={userDetails.email}
                placeholder={intl.formatMessage({ id: "email" })}
                triggerChecking={triggerChecking}
              />
              <InputField
                onChange={(value) => {
                  setUserDetails((prev) => ({
                    ...prev,
                    billingTo: (value ?? "").toString(),
                  }));
                }}
                value={userDetails.billingTo}
                placeholder={intl.formatMessage({ id: "billingTo" })}
                triggerChecking={triggerChecking}
              />
              <InputField
                onChange={(value) => {
                  setUserDetails((prev) => ({
                    ...prev,
                    billingAddress: (value ?? "").toString(),
                  }));
                }}
                value={userDetails.billingAddress}
                placeholder={intl.formatMessage({ id: "billingAddress" })}
                triggerChecking={triggerChecking}
              />
              <InputField
                onChange={(value) => {
                  setUserDetails((prev) => ({
                    ...prev,
                    siteAddress: (value ?? "").toString(),
                  }));
                }}
                value={userDetails.siteAddress}
                placeholder={intl.formatMessage({ id: "siteLotNoAddress" })}
                triggerChecking={triggerChecking}
              />
            </div>
          </div>
          <div className="flex justify-center pt-6">
            <button
              className="bg-[#6b5c45] border-2 border-[#3f3525] text-white px-4 py-2 rounded hover:bg-[#3f3525] transition font-semibold"
              type="submit"
              disabled={loading}
            >
              {intl.formatMessage({
                id: loading ? "submitting" : "submit",
              })}
            </button>
          </div>
        </>
      )}
    </form>
  );
};
